import { Bar } from "vue-chartjs";
import { ChartConfig } from "@/config/chart.js";
import { APP_COLORS } from "@/config/app-colors";
import "chartjs-adapter-moment";
import { SENTIMENT_COLOR } from "@/config/sentiment-colors.js";
import { ReviewCategoriesChartConfigMixin } from "@/helpers/mixins/review-categories-chart-config.js";
import { pluralCount } from "@/helpers/helpers";

export default {
  extends: Bar,
  mixins: [ReviewCategoriesChartConfigMixin],
  computed: {
    chartOptions() {
      const vm = this;
      return {
        onHover: function(event, elements) {
          if (!(elements && elements.length)) {
            const tooltipEl = document.getElementById("chartjs-tooltip");
            if (!tooltipEl) return;

            tooltipEl.style.opacity = 0;
          }
        },
        tooltips: {
          enabled: false,
          mode: "x",
          custom: function(tooltipModel) {
            if (!tooltipModel.dataPoints) return;
            const tooltipData = vm.data.tooltip;
            const dataPointIndex = tooltipModel.dataPoints[0].index;
            const basicData = tooltipData.basic[dataPointIndex];
            const comparisonData = tooltipData.comparison[dataPointIndex];

            let tooltipEl = document.getElementById("chartjs-tooltip");
            // Create element on first render
            if (!tooltipEl) {
              tooltipEl = document.createElement("div");

              tooltipEl.id = "chartjs-tooltip";
              tooltipEl.innerHTML =
                "<div style='color: white; border-radius: 20px; background:rgba(54, 60, 86, 0.7); padding: 18px;'></div>";
              document.body.appendChild(tooltipEl);
            }

            // Hide if no tooltip
            if (tooltipModel.opacity === 0) {
              tooltipEl.style.opacity = 0;
              return;
            }
            // Set caret Position
            tooltipEl.classList.remove("above", "below", "no-transform");
            if (tooltipModel.yAlign) {
              tooltipEl.classList.add(tooltipModel.yAlign);
            } else {
              tooltipEl.classList.add("no-transform");
            }

            // Set Text
            if (tooltipModel.body) {
              const titleLines = tooltipModel.title || [];
              const sentimentOrder = [
                "unassigned",
                "positive",
                "neutral",
                "negative"
              ];

              let innerHtml = createTitleHtml(titleLines);
              innerHtml += createCategoryHtml(
                vm.selectedCategory.name,
                tooltipModel.dataPoints[0].datasetIndex > 3
                  ? comparisonData.count
                  : basicData.count
              );
              innerHtml += createSentimentHtml(
                tooltipModel,
                sentimentOrder,
                basicData,
                comparisonData
              );

              const tableRoot = tooltipEl.querySelector("div");
              tableRoot.innerHTML = innerHtml;
            }

            function createTitleHtml(titleLines) {
              const titleIndex =
                tooltipModel.dataPoints[0].datasetIndex > 3 ? 1 : 0;
              return titleLines
                .map(
                  title =>
                    `<div>${vm.$t("message.reviews.categories.period")} <b>${
                      title.split(";")[titleIndex]
                    }</b></div>`
                )
                .join("");
            }

            function createCategoryHtml(categoryName, count) {
              return `<div style='margin: 16px 0; font-weight: bold;'>${categoryName}: ${pluralCount(
                "message.pluralForms.review",
                count
              )}</div><div>`;
            }

            function createSentimentHtml(
              tooltipModel,
              sentimentOrder,
              basicData,
              comparisonData
            ) {
              return tooltipModel.dataPoints
                .reverse()
                .map(({ value, datasetIndex }, i) => {
                  const sentimentData = getSentimentData(
                    datasetIndex,
                    basicData,
                    comparisonData
                  );
                  const sentiments = sortSentiments(
                    sentimentData,
                    sentimentOrder
                  );
                  const sentimentName = getSentimentName(sentiments, i);
                  const percentage = getPercentage(sentiments, i);
                  return createHtml(value, sentimentName, percentage);
                })
                .join("");
            }

            function getSentimentData(datasetIndex, basicData, comparisonData) {
              return datasetIndex < 4
                ? basicData.sentiment
                : comparisonData.sentiment;
            }

            function sortSentiments(sentimentData, sentimentOrder) {
              const sentiments = Object.entries(sentimentData);
              return sentiments.sort(
                (a, b) =>
                  sentimentOrder.indexOf(a[0]) - sentimentOrder.indexOf(b[0])
              );
            }

            function getSentimentName(sentiments, index) {
              return vm.$t("message.sentiment." + sentiments[index][0]);
            }

            function getPercentage(sentiments, index) {
              return Math.round(sentiments[index][1].percentage);
            }

            function createHtml(value, sentimentName, percentage) {
              return !isNaN(value)
                ? `<div>${sentimentName}: <b> ${value} (${percentage}%)</b></div>`
                : ``;
            }

            const position = this._chart.canvas.getBoundingClientRect();

            tooltipEl.style.opacity = 1;
            tooltipEl.style.position = "absolute";

            const distanceToRight =
              window.innerWidth -
              (position.left + window.pageXOffset + tooltipModel.caretX);

            if (distanceToRight < tooltipEl.offsetWidth) {
              tooltipEl.style.left =
                position.left +
                window.pageXOffset +
                tooltipModel.caretX -
                tooltipEl.offsetWidth +
                "px";
            } else {
              tooltipEl.style.left =
                position.left + window.pageXOffset + tooltipModel.caretX + "px";
            }

            tooltipEl.style.top =
              position.top + window.pageYOffset + tooltipModel.caretY + "px";
            tooltipEl.style.fontFamily = tooltipModel._bodyFontFamily;
            tooltipEl.style.fontSize = tooltipModel.bodyFontSize + "px";
            tooltipEl.style.fontStyle = tooltipModel._bodyFontStyle;
            tooltipEl.style.padding =
              tooltipModel.yPadding + "px " + tooltipModel.xPadding + "px";
            tooltipEl.style.pointerEvents = "none";
          }
        },
        layout: {
          padding: {
            top: 10
          }
        },
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: false },
        scales: {
          yAxes: [
            {
              stacked: true,
              gridLines: {
                display: true,
                drawBorder: false
              },
              ticks: {
                display: true,
                fontSize: ChartConfig.fontSize,
                padding: 0,
                fontFamily: ChartConfig.fontFamily,
                fontColor: ChartConfig.fontColor,
                callback: function(value) {
                  if (Math.floor(value) === value) {
                    return value;
                  }
                }
              },
              display: true
            }
          ],
          xAxes: [
            {
              stacked: true,
              id: "x",
              type: "category",

              gridLines: {
                display: this.gridLines,
                drawBorder: false
              },

              ticks: {
                display: true,
                fontSize: ChartConfig.fontSize,
                padding: 0,
                backdropPadding: 0,
                fontFamily: ChartConfig.fontFamily,
                fontColor: this.$route.query.compareOption
                  ? APP_COLORS.PRIMARY
                  : ChartConfig.fontColor,
                callback: value => this.tickCallback(value, 0)
              },
              display: true
            }
          ]
        }
      };
    }
  },

  methods: {
    tickCallback(value, index) {
      const result = value.split(";")[index];

      return result.length > 10 ? `${result.substr(0, 10)}...` : result;
    },
    commonDatasetConfig(data) {
      return {
        fill: true,
        clip: 3,
        spanGaps: true,
        data
      };
    },
    processData(data) {
      return data.reduce(
        (acc, item) => {
          item.forEach((element, index) => {
            acc[index].push(element);
          });
          return acc;
        },
        [[], [], [], []]
      );
    },
    createDataset({ mainData, tooltipData, backgroundColor, stackName }) {
      return {
        ...this.commonDatasetConfig(mainData),
        tooltipData,
        backgroundColor,
        xAxisID: "x",
        stack: stackName
      };
    },

    render() {
      const backgroundColors = Object.values(SENTIMENT_COLOR);

      const basic = this.processData(this.data.basic);
      const comparison = this.data.comparison.length
        ? this.processData(this.data.comparison)
        : [];

      const dataBasic = backgroundColors.map((color, index) => {
        return this.createDataset({
          mainData: basic[index],
          tooltipData: this.data.tooltip.basic[index],
          backgroundColor: color,
          stackName: "stack0",
          labe: "AAA"
        });
      });

      const comparisonData = this.data.comparison.length
        ? backgroundColors.map((color, index) => {
            return this.createDataset({
              mainData: comparison[index],
              tooltipData: this.data.tooltip.comparison[index],
              backgroundColor: color,
              stackName: "stack1",
              labe: "BBB"
            });
          })
        : [];

      this.renderChart(
        {
          labels: this.labels,
          datasets: [...dataBasic, ...comparisonData]
        },
        this.chartOptions
      );
    }
  }
};
